import { EditOutlined, PlusOutlined } from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import { Button, Flex, Select, Table } from 'antd'
import { ColumnType } from 'antd/es/table'
import { MenuProps } from 'antd/lib'
import { useState } from 'react'

import {
  useStocksServiceDeleteGroupEndpoint,
  useStocksServiceGetGroupListEndpoint,
  useStocksServiceGetGroupListEndpointKey
} from 'src/api/queries'
import {
  ApiError,
  GetGroupListResponseItem,
  ProductGroupCategory
} from 'src/api/requests'
import {
  DeleteConfirm,
  message,
  PageBreadcrumbs,
  SearchBar,
  TableActionButtons
} from 'src/components'
import { useTablePagination } from 'src/hooks'
import { useLocales } from 'src/locales'
import { formErrorHandler, resetPagination } from 'src/utils'

import { StockGroupsModal } from '../components'

export const StockGroups = () => {
  const { t, currentLang } = useLocales()
  const queryClient = useQueryClient()

  const pagination = useTablePagination()
  const [search, setSearch] = useState<string>()
  const [category, setCategory] = useState<ProductGroupCategory>()
  const [isOpen, setIsOpen] = useState(false)
  const [currentId, setCurrentId] = useState<string>()

  const queryParams = {
    pageNumber: pagination.current,
    pageSize: pagination.pageSize,
    ...(category && { category }),
    ...(!!search && {
      searchTerm: search
    })
  }

  const { data, isLoading } = useStocksServiceGetGroupListEndpoint(
    queryParams,
    [queryParams]
  )

  const { mutate } = useStocksServiceDeleteGroupEndpoint({
    onError: (err: ApiError) => formErrorHandler(err.body.errors),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [useStocksServiceGetGroupListEndpointKey]
      })
      message.success(t(''))
    }
  })

  const getItems = (id: string): MenuProps['items'] => [
    {
      key: 'edit',
      label: t('common.edit'),
      icon: <EditOutlined />,
      onClick: () => {
        setCurrentId(id)
        setIsOpen(true)
      }
    },
    {
      key: 'delete',
      danger: true,
      label: (
        <DeleteConfirm
          title={t('stock.delete_group_title')}
          onConfirm={() =>
            mutate({
              id: String(id)
            })
          }
        />
      )
    }
  ]

  const columns: ColumnType<GetGroupListResponseItem>[] = [
    {
      key: 'name',
      title: t('classifiers.title'),
      render: (_, { name }) => name?.[currentLang.value]
    },
    {
      key: 'category',
      title: t('stock.stock_category'),
      render: (_, { category }) => t(`common.${category}`)
    },
    {
      key: 'actions',
      render: ({ id }) => (
        <TableActionButtons justify="flex-end" items={getItems(id)} />
      )
    }
  ]

  const onCategoryChange = (val: ProductGroupCategory) => {
    setCategory(val)
    resetPagination(pagination)
  }

  const productGroupCategories = [
    {
      label: t('common.medicines'),
      value: ProductGroupCategory.MEDICINES
    },
    {
      label: t('common.pharmaceutics'),
      value: ProductGroupCategory.PHARMACEUTICS
    }
  ]

  const onSearch = (val: string) => {
    setSearch(val)
    resetPagination(pagination)
  }

  const onModalClose = () => {
    onToggle()
    setCurrentId(undefined)
  }

  const onToggle = () => setIsOpen((prev) => !prev)

  return (
    <PageBreadcrumbs
      title={t('menu.stock_groups')}
      items={[{ title: t('menu.stock_groups') }]}
    >
      <Flex vertical gap={24}>
        <Flex gap={24}>
          <SearchBar
            className="full-width"
            placeholder={t('transfer.search_placeholder')}
            onSearch={onSearch}
          />
          <Select
            placeholder={t('stock.stock_category')}
            options={productGroupCategories}
            value={category}
            onChange={onCategoryChange}
          />

          <Button icon={<PlusOutlined />} type="primary" onClick={onToggle}>
            {t('stock.create_group')}
          </Button>
        </Flex>

        <Table
          rowKey="id"
          loading={isLoading}
          columns={columns}
          dataSource={data?.data}
          scroll={{ x: 'max-content' }}
          pagination={{ ...pagination, total: data?.totalCount }}
        />
      </Flex>

      {isOpen && <StockGroupsModal onCancel={onModalClose} id={currentId} />}
    </PageBreadcrumbs>
  )
}
